import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPayoutStatus, requestPayout } from '../utils/api';
import { notify } from '../utils/notify';
import './Payments.css';

function Payments() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [payoutStatus, setPayoutStatus] = useState(null);
    const [showPayoutModal, setShowPayoutModal] = useState(false);
    const [paypalEmail, setPaypalEmail] = useState('');
    const [payoutLoading, setPayoutLoading] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user?.id) {
            loadPayoutStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const loadPayoutStatus = async () => {
        try {
            setLoading(true);
            const data = await getPayoutStatus(user.id);
            setPayoutStatus(data);
            if (data.paypalEmail) {
                setPaypalEmail(data.paypalEmail);
            }
        } catch (error) {
            console.error('Error loading payout status:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRequestPayout = async () => {
        if (!paypalEmail || !paypalEmail.includes('@')) {
            await notify.warning('Invalid email', 'Please enter a valid PayPal email address.');
            return;
        }

        setPayoutLoading(true);
        try {
            await requestPayout(user.id, paypalEmail);
            notify.success('Success', 'Payout request submitted.');
            setShowPayoutModal(false);
            await loadPayoutStatus();
        } catch (error) {
            await notify.error('Request failed', error.response?.data?.message || 'Error submitting payout request');
            console.error('Error requesting payout:', error);
        } finally {
            setPayoutLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="payments-page">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    return (
        <div className="payments-page">
            <header className="payments-header">
                <button className="btn-back" onClick={() => navigate('/female-dashboard')}>
                    ← Back to Dashboard
                </button>
                <h1>Payments & Payouts</h1>
            </header>

            <main className="payments-main">
                {payoutStatus && (
                    <>
                        <div className="earnings-card">
                            <h2>Total Earnings</h2>
                            <p className="earnings-amount">${payoutStatus.totalEarnings?.toFixed(2) || '0.00'}</p>
                            {payoutStatus.pendingPayout > 0 && (
                                <p className="pending-payout">
                                    Pending: ${payoutStatus.pendingPayout.toFixed(2)}
                                </p>
                            )}
                            <p className="available-earnings">
                                Available: ${payoutStatus.availableEarnings?.toFixed(2) || '0.00'}
                            </p>
                        </div>

                        <div className="payout-actions-card">
                            <h2>Request Payout</h2>
                            {payoutStatus.pendingRequest ? (
                                <div className="pending-request-info">
                                    <p className="pending-request">
                                        Payout request pending (${payoutStatus.pendingRequest.amount.toFixed(2)})
                                    </p>
                                    <p className="request-date">
                                        Requested: {new Date(payoutStatus.pendingRequest.requestedAt).toLocaleDateString()}
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {payoutStatus.canRequestPayout ? (
                                        <button 
                                            className="btn-request-payout"
                                            onClick={() => setShowPayoutModal(true)}
                                        >
                                            Request Payout
                                        </button>
                                    ) : (
                                        <p className="min-payout-notice">
                                            Minimum ${payoutStatus.minimumPayoutLimit || 30} required for payout
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {payoutStatus.recentRequests && payoutStatus.recentRequests.length > 0 && (
                            <div className="recent-payouts-card">
                                <h2>Recent Payout Requests</h2>
                                <div className="recent-payouts-list">
                                    {payoutStatus.recentRequests.map(req => (
                                        <div key={req._id} className="recent-payout-item">
                                            <div className="payout-item-info">
                                                <span className="payout-amount">${req.amount.toFixed(2)}</span>
                                                <span className="payout-date">
                                                    {new Date(req.requestedAt).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <span className={`payout-status-badge ${req.status}`}>
                                                {req.status === 'pending' ? 'Pending' : 
                                                 req.status === 'completed' ? 'Paid' : req.status}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Payout Request Modal */}
            {showPayoutModal && (
                <div className="payout-modal">
                    <div className="payout-modal-content">
                        <h2>Request Payout</h2>
                        <p className="payout-info-text">
                            Minimum payout amount is ${payoutStatus?.minimumPayoutLimit || 30}. You can request payout for your available earnings.
                        </p>
                        {payoutStatus && (
                            <p className="available-amount">
                                Available: ${payoutStatus.availableEarnings?.toFixed(2) || '0.00'}
                            </p>
                        )}
                        <div className="form-group">
                            <label>PayPal Email</label>
                            <input
                                type="email"
                                value={paypalEmail}
                                onChange={(e) => setPaypalEmail(e.target.value)}
                                placeholder="your-email@example.com"
                                required
                            />
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn-cancel"
                                onClick={() => setShowPayoutModal(false)}
                                disabled={payoutLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-submit"
                                onClick={handleRequestPayout}
                                disabled={payoutLoading || !paypalEmail}
                            >
                                {payoutLoading ? 'Submitting...' : 'Submit Request'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Payments;
